
import { sentryService } from '@/services/SentryService';
import { isReloadGuardTripped } from '@/reloadLoopGuard';
declare const LEANLAW_API: string;

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const VISIBILITY_RECHECK_THROTTLE_MS = 60 * 1000;
const MAX_HIDDEN_BASELINE_ATTEMPTS = 3;
const NONE_ETAG = '"none"';

interface ReloadStatus {
  cleared?: boolean;
  forceTimeSeconds?: number | null;
  message?: string;
}

let lastSeenEtag: string | null = null;

let activeTeardown: (() => void) | null = null;

function resetPrompt(): void {
  activeTeardown?.();
  activeTeardown = null;
}

let lastFailureKey: string | null = null;
function reportPollFailure(key: string, report: () => void): void {
  if (lastFailureKey === key) return;
  lastFailureKey = key;
  report();
}

function createFloatingCard(opts: {
  id: string;
  title: string;
  message: string;
  dismissible: boolean;
  accentColor: string;
  countdownText?: string;
}): { card: HTMLDivElement; countdown: HTMLParagraphElement | null } {
  const card = document.createElement('div');
  card.setAttribute('id', opts.id);
  card.style.cssText =
    'position:fixed;bottom:24px;left:24px;z-index:9999;' +
    'background:#fff;border:1px solid #ddd;border-radius:8px;' +
    'box-shadow:0 4px 20px rgba(0,0,0,0.15);' +
    'padding:16px 20px;font-family:inherit;font-size:14px;color:#333;' +
    'max-width:320px;width:calc(100vw - 48px);';

  const header = document.createElement('div');
  header.style.cssText =
    'display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:10px;';

  const title = document.createElement('strong');
  title.textContent = opts.title;
  title.style.cssText = 'font-size:14px;color:#111;';
  header.appendChild(title);

  if (opts.dismissible) {
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕';
    closeBtn.setAttribute('aria-label', 'Dismiss');
    closeBtn.style.cssText =
      'flex-shrink:0;background:none;border:none;cursor:pointer;' +
      'font-size:13px;color:#888;padding:0;line-height:1;margin-top:1px;';
    closeBtn.addEventListener('click', () => resetPrompt());
    header.appendChild(closeBtn);
  }

  const msg = document.createElement('p');
  msg.textContent = opts.message;
  msg.style.cssText = 'margin:0 0 6px;color:#555;font-size:13px;line-height:1.5;';

  let countdown: HTMLParagraphElement | null = null;
  if (opts.countdownText !== undefined) {
    countdown = document.createElement('p');
    countdown.textContent = opts.countdownText;
    countdown.style.cssText = 'margin:0 0 14px;color:#555;font-size:13px;line-height:1.5;font-weight:600;';
  }

  const reloadBtn = document.createElement('button');
  reloadBtn.textContent = 'Reload Now';
  reloadBtn.style.cssText =
    `width:100%;padding:7px 14px;` +
    `background:${opts.accentColor};border:none;border-radius:5px;` +
    `cursor:pointer;font-size:13px;color:#fff;font-family:inherit;font-weight:500;`;
  reloadBtn.addEventListener('click', () => window.location.reload());

  card.appendChild(header);
  card.appendChild(msg);
  if (countdown) card.appendChild(countdown);
  card.appendChild(reloadBtn);
  document.body.appendChild(card);

  return { card, countdown };
}

function showSoftBanner(message?: string): void {
  const { card } = createFloatingCard({
    id: 'll-update-banner',
    title: 'Update available',
    message: message || 'A new version of LeanLaw is available. Reload to get the latest.',
    dismissible: true,
    accentColor: '#29bf12',
  });

  activeTeardown = () => card.remove();
}

function showForceBlockingOverlay(message?: string): void {
  const previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  const overlay = document.createElement('div');
  overlay.setAttribute('id', 'll-force-reload-overlay');
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:10000;' +
    'background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;';

  const card = document.createElement('div');
  card.style.cssText =
    'background:#fff;border-radius:8px;max-width:420px;width:calc(100vw - 48px);' +
    'font-family:inherit;text-align:center;overflow:hidden;';

  const cardHeader = document.createElement('div');
  cardHeader.style.cssText = 'background:#fee2e2;color:#991b1b;padding:16px;font-size:17px;font-weight:600;';
  cardHeader.textContent = 'LeanLaw was updated';

  const body = document.createElement('div');
  body.style.cssText = 'padding:24px;color:#333;font-size:14px;line-height:1.6;';
  body.textContent = message || 'This page needs to reload to continue.';

  const actions = document.createElement('div');
  actions.style.cssText = 'padding:0 24px 24px;';

  const reloadBtn = document.createElement('button');
  reloadBtn.textContent = 'Reload Now';
  reloadBtn.style.cssText =
    'min-width:160px;padding:10px 20px;background:#dc2626;border:none;border-radius:5px;' +
    'cursor:pointer;font-size:14px;color:#fff;font-family:inherit;font-weight:500;';
  reloadBtn.addEventListener('click', () => window.location.reload());

  actions.appendChild(reloadBtn);
  card.appendChild(cardHeader);
  card.appendChild(body);
  card.appendChild(actions);
  overlay.appendChild(card);
  document.body.appendChild(overlay);

  const reloadTimeoutId = window.setTimeout(() => window.location.reload(), 300);

  activeTeardown = () => {
    window.clearTimeout(reloadTimeoutId);
    document.body.style.overflow = previousOverflow;
    overlay.remove();
  };
}

function showForceCountdownCard(forceTimeSeconds: number, message?: string): void {
  if (forceTimeSeconds <= 0) {
    showForceBlockingOverlay(message);
    return;
  }

  const deadline = Date.now() + forceTimeSeconds * 1000;
  const countdownLabel = (secondsLeft: number) => `This page will reload in ${secondsLeft}s.`;

  const { card, countdown } = createFloatingCard({
    id: 'll-force-reload-card',
    title: 'LeanLaw was updated',
    message: message || 'This page needs to reload to continue.',
    dismissible: false,
    accentColor: '#dc2626',
    countdownText: countdownLabel(Math.max(0, Math.ceil((deadline - Date.now()) / 1000))),
  });

  const renderTick = () => {
    const secondsLeft = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
    countdown!.textContent = countdownLabel(secondsLeft);
    if (Date.now() >= deadline) {
      resetPrompt();
      showForceBlockingOverlay(message);
    }
  };
  const intervalId = window.setInterval(renderTick, 1000);

  activeTeardown = () => {
    window.clearInterval(intervalId);
    card.remove();
  };
}

let inFlight = false;
let lastCheckAt = 0;
let hiddenBaselineAttempts = 0;

async function checkReloadStatus(): Promise<void> {
  if (inFlight) return;
  if (document.hidden) {
    // Baseline established - a hidden tab cannot act on a prompt, so stop polling.
    if (lastSeenEtag !== null) return;
    // No baseline yet, which means this tab loaded in the background. It needs one:
    // without it, the poll on return would baseline against a trigger raised while
    // the tab was away and never prompt at all.
    if (hiddenBaselineAttempts >= MAX_HIDDEN_BASELINE_ATTEMPTS) return;
    hiddenBaselineAttempts += 1;
  }
  inFlight = true;
  lastCheckAt = Date.now();
  try {
    const headers: Record<string, string> = {};
    if (lastSeenEtag !== null) {
      headers['If-None-Match'] = lastSeenEtag;
    }

    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), 30_000);
    try {
      const res = (await sentryService.startNewTrace(() =>
        fetch(`${LEANLAW_API}/reload-status`, {
          cache: 'no-store',
          headers,
          signal: timeoutController.signal,
        }),
      )) as Response;

      if (res.status === 304) {
        lastFailureKey = null;
        return;
      }
      if (!res.ok) {
        reportPollFailure(`http-${res.status}`, () =>
          sentryService.captureException(new Error(`/reload-status returned ${res.status}`), {
            feature: 'reload-status-poll',
            status: res.status,
          }),
        );
        return;
      }

      const etag = res.headers.get('ETag');
      if (!etag) {
        reportPollFailure('missing-etag', () =>
          sentryService.captureException(new Error('/reload-status response missing ETag header'), {
            feature: 'reload-status-poll',
          }),
        );
        return; // ETag missing (e.g. CORS exposed-headers misconfigured) - nothing safe to compare against
      }
      lastFailureKey = null;

      if (lastSeenEtag === null) {
        // First poll of this tab's lifetime: baseline only, never prompt for whatever was
        // already active before this tab loaded (it's already running the latest code).
        lastSeenEtag = etag;
        return;
      }

      if (etag === lastSeenEtag) return;

      const status = (await res.json()) as ReloadStatus;
      lastSeenEtag = etag;
      resetPrompt();
      if (status.cleared || etag === NONE_ETAG) return;
      if (status.forceTimeSeconds == null) {
        showSoftBanner(status.message);
      } else {
        showForceCountdownCard(status.forceTimeSeconds, status.message);
      }
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    const err = error as Error;
    reportPollFailure(`network-error:${err?.name}`, () =>
      sentryService.captureMessage('/reload-status poll failed (network error)', 'warning', {
        feature: 'reload-status-poll',
        errorName: err?.name,
        errorMessage: err?.message,
      }),
    );
  } finally {
    inFlight = false;
  }
}

function onVisibilityChange(): void {
  if (document.hidden) return;
  if (Date.now() - lastCheckAt < VISIBILITY_RECHECK_THROTTLE_MS) return;
  checkReloadStatus();
}

// This banner can reload the page itself, so it must stay off a page the loop guard stopped.
if (!isReloadGuardTripped()) {
  document.addEventListener('visibilitychange', onVisibilityChange);

  checkReloadStatus();
  setInterval(checkReloadStatus, POLL_INTERVAL_MS);
}
